import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, UserRound } from 'lucide-react';
import { SheetLateral } from './SheetLateral';
import CachedImg from './CachedImg';
import { ModeLockOverlay } from '../pages/v2/components/HomeCards';
import { ModeExamplesModal } from '../pages/v2/components/ModeExamplesModal';
import {
  MODES,
  anuncioTercero,
  modosVisibles,
  PORTADA_CUADRADA,
  portadaCuadradaDeRubro,
  nombreCortoModo,
  caratulaModo,
} from '../pages/v2/modesCatalog';
import { HERRAMIENTAS_EXTRA } from '../pages/estilos/contenidoAtlas';
import { useCuentaShell } from '../pages/estilos/EstiloMinimal';
import { getModePlanLock } from '../lib/planCapabilities';
import { getUserPlan } from '../lib/planUtils';
import {
  rubroVePlantillas,
  rubroVePlantillasBelleza,
  esRubroBelleza,
  esRubroHogarDeco,
  esRubroModa,
} from '../lib/rubro';
import { useUserGroup } from '../hooks/useUserGroup';
// En mobile este drawer es el sheet de "Generar"
// (ADR-2026-08-04-desde-marca-se-abre-el-sheet-de-generar).
import { useEsMobile } from '../hooks/useEsMobile';
import { SheetGenerar } from '../pages/v2/components/SheetGenerar';
import { useModosGenerar } from '../pages/v2/useModosGenerar';
import type { ModelV2 } from '../pages/v2/modelsApi';
import type { MockProductV2 } from '../pages/v2/mockData';
import type { PresetV2 } from '../pages/v2/mockData';
import { palabraDeModeloActual } from '../lib/nombreDeModelo';

/**
 * Sheet "¿Qué querés generar con este producto?" —
 * ADR-2026-08-04-desde-un-producto-se-elige-el-modo.
 *
 * Se abre al tocar un producto en "Editar marca → Mis productos" y ofrece los mismos
 * modos que la tira del inicio, con el mismo orden, los mismos nombres cortos y el
 * mismo candado por plan. Es el drawer del picker de Formato (`SheetLateral`) con
 * otro contenido — ADR-2026-08-04-el-drawer-de-formato-se-comparte.
 *
 * Por qué se rearma la lista acá en vez de reusar la de la home: esa sale de
 * `design-row`, una colección que `InterfazV2` construye en su propio render y que
 * fuera de esa pantalla no existe. Lo que sí se comparte son las TRES fuentes que la
 * definen —`MODES` con sus feature flags, `anuncioTercero` para el orden y
 * `getModePlanLock` para el candado—, que es el mismo trío que consume `/modos`.
 */

/** Una opción del sheet: un modo de `MODES`, o Plantillas (que no es un modo). */
interface OpcionModo {
  clave: string;
  titulo: string;
  /** Póster de la carátula. Vacío en Anuncio: su portada es el primer cuadro del video. */
  imagen: string;
  /** Carátula en video, cuando el modo tiene una. */
  video?: string;
  bloqueado: boolean;
  /** `null` en Plantillas: no abre un modo sino el catálogo. */
  preset: PresetV2 | null;
  ruta: string;
}

/**
 * Sobre qué se está decidiendo el modo: el producto o el modelo que se tocó en la
 * grilla. Es lo único que cambia entre las dos aperturas del sheet —la cabecera, qué
 * modos se ofrecen y qué se precarga al entrar—, así que va como un discriminado y no
 * como dos props opcionales que podrían venir las dos a la vez.
 */
export type SujetoDeGeneracion =
  | { tipo: 'producto'; producto: MockProductV2 }
  | { tipo: 'modelo'; modelo: ModelV2 };

/**
 * La pregunta de la cabecera, nombrando lo que se tocó —
 * ADR-2026-08-05-el-sheet-de-generar-nombra-el-sujeto.
 *
 * Dice "este producto" / "este modelo" y no el nombre propio porque el nombre ya está
 * abajo, en la fila con la miniatura: repetirlo arriba no agrega nada y hace un título
 * de largo impredecible.
 */
function tituloDelSujeto(sujeto: SujetoDeGeneracion): string {
  return sujeto.tipo === 'modelo'
    ? // "Modelo" en Moda, "Avatar" en el resto — la palabra sale del módulo y no de
      // un hook porque esto no es un componente
      // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
      `¿Qué querés generar con este ${palabraDeModeloActual().singular}?`
    : '¿Qué querés generar con este producto?';
}

export function SheetModos({
  sujeto,
  rubro,
  onClose,
}: {
  /** Lo que se tocó en la grilla. `null` = sheet cerrado. */
  sujeto: SujetoDeGeneracion | null;
  /** Rubro de la marca activa — decide si Plantillas está en la lista. */
  rubro: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  // Montado dentro del shell nuevo, `abrirPricing` abre el catálogo de planes como
  // overlay; fuera de él (mobile, bajo `DashboardLayout`) es un no-op y hay que
  // navegar a la pantalla de suscripción, que es lo que hace `/modos`.
  const { abrirPricing, dentroDelShell } = useCuentaShell();
  const { isDevTester } = useUserGroup();

  // Plan para el gating, con el mismo arranque que `/modos`: el cache que llena el
  // Sidebar para tener candados desde el primer paint, y el valor real después. Con
  // el plan todavía desconocido NO se bloquea nada — mismo criterio que
  // `getModePlanLock`, que devuelve `null` con plan `null`.
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    // El plan real, sin lentes: el panel CONTEXTO DEV y sus overrides se eliminaron
    // (supersede ADR-2026-08-03-el-plan-simulado-tambien-gatea).
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlan(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  // El sujeto se va en `null` al cerrar y el sheet sigue montado 300ms más por la
  // animación de salida. `esModelo` sale del sujeto vivo y es de lo que depende la
  // lista, en vez del objeto entero: así no se rearma al pasar de un producto a otro.
  const esModelo = sujeto?.tipo === 'modelo';
  // El gemelo, para el otro sujeto —
  // ADR-2026-08-20-desde-un-producto-tambien-hay-modos-que-no-aplican. Sin esto el
  // drawer ofrecía Animar y Carrusel sobre un producto: los dos arrancan eligiendo una
  // imagen YA GENERADA y ni miran el producto que se acaba de tocar.
  const esProducto = sujeto?.tipo === 'producto';

  const esHogarDeco = esRubroHogarDeco(rubro);
  // El gemelo para Belleza y Cuidado Personal (`soloBelleza`) —
  // ADR-2026-09-10-modo-mostrar-como-funciona-belleza.
  const esBelleza = esRubroBelleza(rubro);
  // El gemelo para Moda e Indumentaria (`soloModa`) —
  // ADR-2026-09-17-el-modo-diseno-es-solo-para-moda.
  const esModa = esRubroModa(rubro);
  const opciones = useMemo<OpcionModo[]>(() => {
    // Los mismos filtros que `/modos`, la home, el sheet de Generar y la sidebar, ahora
    // en un solo lugar —
    // ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares. Sin esto el
    // drawer ofrecía modos que en el resto de la app no existen.
    const modos = modosVisibles(MODES, {
      isDevTester,
      esHogarDeco,
      esBelleza,
      esModa,
      esModelo,
      esProducto,
    }).map((m): OpcionModo => {
      // La carátula sale de `caratulaModo`, la misma fuente que la home, `/modos` y el
      // sheet de Generar (ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo):
      // portada del rubro → pisada de interfaz (la grilla de vistas en Catálogo) →
      // carátula del catálogo → imagen del preset.
      const arte = caratulaModo(m.path, rubro, m.preset.imageUrl);
      // La portada CUADRADA gana cuando el modo tiene una
      // (ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile): las cards de este drawer
      // son `aspect-square` y la carátula de siempre es apaisada, así que el recorte
      // central se comía el encuadre —en Modelo dejaba a la modelo cortada y las tres
      // entradas del collage afuera—. Con portada cuadrada tampoco se reproduce el
      // video: la portada es justamente lo que se quiere ver.
      // La carátula propia del rubro gana sobre las dos
      // (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro): si el rubro tiene una,
      // es la que representa al modo para esa marca, en cualquier forma de caja.
      const cuadrada = portadaCuadradaDeRubro(m.path, rubro) ?? PORTADA_CUADRADA[m.path];
      return {
        clave: m.path,
        titulo: nombreCortoModo(m.path, m.preset.title),
        imagen: cuadrada ?? arte.poster,
        video: cuadrada ? undefined : arte.video,
        bloqueado: !!getModePlanLock(m.preset.kind, plan),
        preset: m.preset,
        // `m.route` primero: los modos con pantalla propia (Carrusel, Animar, Shorts y
        // los tres de Hogar y Decoración) NO los monta `InterfazV2`, así que
        // `/interfaz-v2/modo/<path>` era un click muerto —
        // ADR-2026-08-20-los-modos-con-pantalla-propia-se-abren-por-su-ruta,
        // ADR-2026-09-10-el-sheet-de-generar-respeta-la-ruta-propia-del-modo.
        // El `state` de abajo viaja igual y esas pantallas lo ignoran: arman su flujo
        // desde cero y no leen `presetData` ni los preseleccionados.
        ruta: m.route ?? `/interfaz-v2/modo/${m.path}`,
      };
    });
    // Cuál de los dos modos de Plantillas ve este rubro: el de Moda
    // (ADR-2026-08-03-plantillas-solo-para-moda) o el de Belleza y Cuidado Personal
    // (ADR-2026-09-10-plantillas-belleza-modo-propio). Son excluyentes, así que la
    // card es una sola y cambia a qué pantalla lleva (`plantillas.ruta`).
    const plantillas = HERRAMIENTAS_EXTRA.find(
      (h) =>
        h.id === (rubroVePlantillasBelleza(rubro) ? 'plantillas-belleza' : 'plantillas'),
    );
    // Va al final: no es un modo de generación sino un atajo al catálogo, igual que
    // en la sidebar. Sin candado propio — el catálogo se ve con cualquier plan.
    const conPlantillas =
      plantillas && (rubroVePlantillas(rubro) || rubroVePlantillasBelleza(rubro))
        ? [
            {
              clave: plantillas.id,
              // Nombre y carátula del catálogo, como cualquier otra card de esta grilla
              // (ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo). Antes
              // salían de `HERRAMIENTAS_EXTRA`, que traía otra foto —el hero de
              // Anuncio— que la que muestran la home y `/modos`.
              titulo: nombreCortoModo(plantillas.id, plantillas.nombre),
              imagen: caratulaModo(plantillas.id, rubro, plantillas.poster).poster,
              bloqueado: false,
              preset: null,
              ruta: plantillas.ruta,
            },
          ]
        : [];
    // Anuncio tercero, la misma posición que ocupa en la tira y en la sidebar
    // (ADR-2026-08-03-anuncio-tercero-tira-y-sidebar).
    return [...anuncioTercero(modos, (m) => m.clave), ...conPlantillas];
  }, [plan, rubro, isDevTester, esModelo, esProducto, esModa]);

  const elegir = (opcion: OpcionModo) => {
    // Bloqueado → catálogo de planes, igual que una card con candado en el inicio
    // (ADR-2026-08-03-modo-bloqueado-abre-pricing). El sheet queda abierto detrás
    // del overlay para no perder lo elegido si el usuario lo cierra.
    if (opcion.bloqueado) {
      if (dentroDelShell) abrirPricing('bloqueado');
      else navigate('/subscription');
      return;
    }
    onClose();
    // `presetData` monta el sheet del modo en el primer paint —mismo mecanismo que
    // `/modos`, sin flash de la home—, y `productosPreseleccionados` hace que abra
    // directo en Configurar con el producto puesto, salteando el paso de elegirlo
    // (ADR-2026-08-04-el-producto-entra-con-el-modo). Van juntos: el producto se lee
    // en el initializer, que es lo que permite que el paso saltado no se vea nunca.
    //
    // Desde un modelo va `modeloPreseleccionado` en su lugar
    // (ADR-2026-08-04-desde-un-modelo-se-elige-el-modo): NO saltea ningún paso —el
    // producto sigue faltando y hay que elegirlo— pero el modo abre con ese modelo ya
    // puesto en el picker.
    navigate(opcion.ruta, {
      state: opcion.preset
        ? {
            presetData: opcion.preset,
            collectionPresets: [],
            productosPreseleccionados:
              sujeto?.tipo === 'producto' ? [sujeto.producto] : [],
            modeloPreseleccionado:
              sujeto?.tipo === 'modelo'
                ? {
                    // `mode: 'preset'` = modelo guardado, la misma forma que arma el
                    // ModelPicker al elegirlo de la biblioteca. `thumbnailUrl` y no
                    // `thumbUrl`: la miniatura chica es sólo para grillas, y el
                    // picker guarda la full-res.
                    mode: 'preset' as const,
                    id: String(sujeto.modelo.id),
                    name: sujeto.modelo.nombre,
                    imageUrl: sujeto.modelo.thumbnailUrl,
                  }
                : undefined,
          }
        : undefined,
    });
  };

  // Miniatura y nombre de lo elegido. El producto va cuadrado y el modelo 9/16, el
  // mismo recorte con el que se lo acaba de ver en su grilla.
  const miniatura =
    sujeto?.tipo === 'producto'
      ? sujeto.producto.imageUrl
      : sujeto?.tipo === 'modelo'
        ? sujeto.modelo.thumbUrl
        : null;
  const nombre =
    sujeto?.tipo === 'producto'
      ? sujeto.producto.name
      : sujeto?.tipo === 'modelo'
        ? sujeto.modelo.nombre
        : '';

  // El drawer de desktop NO se desmonta al cerrar: `sujeto` se va en `null` y el panel
  // sigue 300ms en pantalla animando la salida. Si el título saliera del sujeto vivo
  // cambiaría de texto mientras se va, así que se retiene el último. (El sheet de mobile
  // no lo necesita: ahí el llamador desmonta.)
  const ultimoTitulo = useRef('¿Qué querés generar?');
  if (sujeto) ultimoTitulo.current = tituloDelSujeto(sujeto);

  return (
    <SheetLateral open={!!sujeto} onClose={onClose} titulo={ultimoTitulo.current}>
      {/* Lo elegido, arriba de todo: el sheet se abrió desde una grilla de miniaturas
          parecidas entre sí, y sin esto no queda claro sobre cuál se está
          decidiendo. */}
      {sujeto && (
        <div className="mb-4 flex items-center gap-3 rounded-xl bg-black/[0.03] p-2.5">
          <div
            className={`relative shrink-0 overflow-hidden rounded-lg bg-neutral-100 ${
              sujeto.tipo === 'modelo' ? 'h-14 w-8' : 'h-11 w-11'
            }`}
          >
            {miniatura ? (
              <CachedImg
                src={miniatura}
                alt={nombre}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-brand-black/25">
                {sujeto.tipo === 'modelo' ? (
                  <UserRound className="h-5 w-5" strokeWidth={1.5} />
                ) : (
                  <Package className="h-5 w-5" strokeWidth={1.5} />
                )}
              </div>
            )}
          </div>
          <p className="min-w-0 flex-1 truncate text-[14px] font-medium text-brand-black/90">
            {nombre}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {opciones.map((opcion) => (
          <button
            key={opcion.clave}
            type="button"
            onClick={() => elegir(opcion)}
            aria-label={
              opcion.bloqueado ? `${opcion.titulo} — ver planes` : opcion.titulo
            }
            className="relative overflow-hidden rounded-2xl border-2 border-neutral-200 bg-white text-left transition-all hover:border-brand-primary/40 active:scale-[0.98]"
          >
            <div className="relative aspect-square bg-neutral-100">
              {/* Con video se pinta el `<video>` y no el póster, como en la tira: es
                  la única forma de que Anuncio tenga portada —su póster es vacío a
                  propósito y su carátula ES el primer cuadro del video—. Sin
                  `autoPlay`: son siete cards a la vez y acá alcanza el cuadro fijo;
                  `preload="metadata"` es justamente lo que lo dibuja. */}
              {opcion.video ? (
                <video
                  src={opcion.video}
                  poster={opcion.imagen || undefined}
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <img
                  src={opcion.imagen}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
              {opcion.bloqueado && <ModeLockOverlay />}
            </div>
            <p className="truncate px-3 py-2.5 text-[13px] font-medium text-brand-black/90">
              {opcion.titulo}
            </p>
          </button>
        ))}
      </div>
    </SheetLateral>
  );
}

/**
 * Lo que abre tocar un producto o un modelo en Mi marca, según el dispositivo —
 * ADR-2026-08-04-desde-marca-se-abre-el-sheet-de-generar.
 *
 * En MOBILE es el sheet de "Generar", el mismo que el botón central de la barra
 * inferior: primero la pregunta de categoría (Imágenes / Videos) y recién después los
 * modos. Antes acá se listaban los seis modos de una, o sea la misma decisión planteada
 * de dos formas distintas según de dónde se entrara.
 *
 * En DESKTOP sigue el drawer lateral de modos: el sheet de Generar es `sm:hidden` —es
 * una superficie de teléfono, anclada abajo— y ahí no existe.
 *
 * Las "ediciones rápidas" no van en esta entrada: son acciones sobre algo YA generado y
 * no tienen nada que ver con el producto que se acaba de tocar. El sheet las omite solo
 * con recibir la lista vacía.
 */
function DrawerGeneracion({
  sujeto,
  rubro,
  onClose,
}: {
  sujeto: SujetoDeGeneracion | null;
  rubro: string | null;
  onClose: () => void;
}) {
  const esMobile = useEsMobile();
  // Los mismos modos que ofrece la barra inferior, filtrados y precargados con lo que
  // se tocó en la grilla.
  const { modos, ejemplos, cerrarEjemplos, irAPlanes } = useModosGenerar({
    sujeto,
    conPlantillas: rubroVePlantillas(rubro) || rubroVePlantillasBelleza(rubro),
    rubro,
  });

  if (!esMobile) return <SheetModos sujeto={sujeto} rubro={rubro} onClose={onClose} />;
  // El sheet de Generar no tiene estado `open`: lo monta y desmonta el llamador. El
  // visor de ejemplos va AFUERA de esa condición: tocar una card bloqueada cierra el
  // sheet (el llamador pone `sujeto` en null) y el visor tiene que sobrevivirlo —
  // ADR-2026-08-25-sheet-generar-fixes-mobile.
  return (
    <>
      {sujeto && (
        <SheetGenerar
          modos={modos}
          ediciones={[]}
          titulo={tituloDelSujeto(sujeto)}
          onClose={onClose}
        />
      )}
      <ModeExamplesModal
        open={ejemplos !== null}
        onClose={cerrarEjemplos}
        title={ejemplos?.title ?? ''}
        description={ejemplos?.description}
        examples={ejemplos?.examples ?? []}
        actionLabel="Ver planes"
        onAction={irAPlanes}
        locked
        planNote={ejemplos?.planNote}
      />
    </>
  );
}

/**
 * Envoltorio para la grilla de productos, que trabaja con `MockProductV2` y no tiene
 * por qué saber del discriminado.
 */
export function SheetModosProducto({
  producto,
  rubro,
  onClose,
}: {
  producto: MockProductV2 | null;
  rubro: string | null;
  onClose: () => void;
}) {
  const sujeto = useMemo<SujetoDeGeneracion | null>(
    () => (producto ? { tipo: 'producto', producto } : null),
    [producto],
  );
  return <DrawerGeneracion sujeto={sujeto} rubro={rubro} onClose={onClose} />;
}

/** Idem para la grilla de modelos. */
export function SheetModosModelo({
  modelo,
  rubro,
  onClose,
}: {
  modelo: ModelV2 | null;
  rubro: string | null;
  onClose: () => void;
}) {
  const sujeto = useMemo<SujetoDeGeneracion | null>(
    () => (modelo ? { tipo: 'modelo', modelo } : null),
    [modelo],
  );
  return <DrawerGeneracion sujeto={sujeto} rubro={rubro} onClose={onClose} />;
}

export default SheetModosProducto;
